import React from "react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { VehicleType } from "@/types/collections"

/** Nothing ticked means no restriction, returned as null (the columns' "any"). */
interface VehicleTypePickerProps {
  id?: string
  value: string[] | null | undefined
  onChange: (value: string[] | null) => void
  vehicleTypes: VehicleType[]
  emptyLabel: string
  className?: string
}

const VehicleTypePicker: React.FC<VehicleTypePickerProps> = ({
  id,
  value,
  onChange,
  vehicleTypes,
  emptyLabel,
  className,
}) => {
  const selected = value ?? []
  // A stored name with no class behind it (renamed, or deleted) stays visible so it can be removed.
  const names = [
    ...vehicleTypes.filter((vt) => vt.is_active || selected.includes(vt.name)).map((vt) => vt.name),
    ...selected.filter((name) => !vehicleTypes.some((vt) => vt.name === name)),
  ]

  const toggle = (name: string, on: boolean) => {
    const next = on ? [...selected, name] : selected.filter((n) => n !== name)
    onChange(next.length ? next : null)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn("w-full justify-start bg-white font-normal truncate", className)}
        >
          {selected.length ? selected.join(", ") : emptyLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent variant="menu" align="start">
        <div className="space-y-1">
          {names.map((name) => (
            <label
              key={name}
              className="flex items-center gap-2 p-1.5 rounded hover:bg-palette-jungleGreen000 cursor-pointer text-xs"
            >
              <Checkbox
                checked={selected.includes(name)}
                onCheckedChange={(checked) => toggle(name, checked === true)}
              />
              {name}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default VehicleTypePicker
